// Filter Bar: the row above a table with a filters dropdown, a search field and the
// applied filters below it as dismissible tags.
//
// In Ember the filter state lives in the application: @filters goes in, @onFilter comes
// out. In HTML the state lives where HTML keeps state — in the form controls. The bar is a
// GET form (or sits in one): every filter is a real <input>/<select> with a name, so
// "Apply filters" submits ?project=project-1&version=1.0 and the server renders the page
// back with those controls checked. The applied tags are read off the controls, so the
// server never describes the same filters twice.
//
// i80-filter fires whenever the applied filters change and is cancelable: an app that
// re-fetches by itself (HTMX, fetch) calls preventDefault() and nothing is submitted.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";
import { I80Dropdown } from "./dropdown.js";

const T = {
  toggle: "View applied filters",
  filters: "Filters",
  apply: "Apply filters",
  clearAll: "Clear all filters",
  clearFilter: "Clear filter",
  clearSelection: "Clear selection",
  none: "No filters applied",
  live: "Filters will be applied automatically as selections are made",
  tabs: "Filter bar tabs",
  applied: "Filters applied",
  dismiss: "Clear filter",
  search: "Search",
  groupSearch: "Search filter options",
  selectionLegend: "Filter by options",
  numberLegend: "Filter by number",
  numberLabel: "Number is",
  numberValue: "Number value",
  numberStart: "Number start value",
  numberEnd: "Number end value",
  numberPlaceholder: "Enter a value",
  dateLegend: "Filter by date",
  dateLabel: "Date is",
  dateValue: "Date value",
  dateStart: "Date start value",
  dateEnd: "Date end value",
  pick: "Pick a selector",
  start: "Start",
  end: "End",
  and: "and",
};

// The operators, as the Ember filter groups spell them: value, option label, and the symbol
// or word the applied filter tag uses.
const NUMERICAL = [
  ["less-than", "Less than (<)", "<"],
  ["less-than-or-equal-to", "Less than or equal to (≤)", "≤"],
  ["equal-to", "Equal to (=)", "="],
  ["greater-than-or-equal-to", "Greater than or equal to (≥)", "≥"],
  ["greater-than", "Greater than (>)", ">"],
  ["between", "Between", "between"],
];
const DATE = [
  ["before", "Before", "before"],
  ["exactly", "Exactly", "exactly"],
  ["after", "After", "after"],
  ["between", "Between", "between"],
];

const GROUP_TYPES = ["multi-select", "single-select", "numerical", "date", "time", "datetime", "generic"];
const SELECTION = ["multi-select", "single-select"];
const INPUT_TYPE = { date: "date", time: "time", datetime: "datetime-local" };

/** A <button> with the i80 button classes, as the Ember Button renders it. */
function button(label, { color = "primary", size = "small", icon: name = null, iconOnly = false, type = "button", extra = "", ...attrs } = {}) {
  const el = h("button", {
    class: ["i80-button", `i80-button--color-${color}`, iconOnly && "i80-button--is-icon-only", `i80-button--size-${size}`, extra].filter(Boolean).join(" "),
    type,
    ...attrs,
  });
  const glyph = name ? h("span", { class: "i80-button__icon" }, icon(name, { size: size === "large" ? "24" : "16", stretched: true })) : null;
  if (iconOnly && glyph) {
    el.setAttribute("aria-label", label);
    el.append(glyph);
  } else {
    el.append(...(glyph ? [glyph] : []), h("span", { class: "i80-button__text" }, label));
  }
  return el;
}

function textInput(attrs, extra = "") {
  return h("input", { class: ["i80-form-text-input", "i80-typography-body-200", "i80-font-weight-regular", extra].filter(Boolean).join(" "), ...attrs });
}

/** A labelled <select>, the markup of the Ember Form::Select::Field. */
function selectField(label, attrs, options, selected) {
  const id = nextId("i80-filter-selector");
  const select = h("select", { class: "i80-form-select i80-typography-body-200 i80-font-weight-regular i80-filter-bar__filter-group__field", id, ...attrs });
  select.append(h("option", { value: "" }, T.pick));
  for (const [value, text] of options) select.append(h("option", { value, selected: value === selected || null }, text));
  return h("div", { class: "i80-form-field--layout-vertical" },
    h("label", { class: "i80-form-label i80-form-field__label", for: id, id: `label-${id}` }, label),
    h("div", { class: "i80-form-field__control" }, select));
}

/** What a control contributes to the query string; "" when it contributes nothing. */
function valueOf(control) {
  if (control.type === "checkbox" || control.type === "radio") return control.checked ? control.value : "";
  return control.value ?? "";
}

function clearControl(control) {
  if (control.type === "checkbox" || control.type === "radio") control.checked = false;
  else control.value = "";
}

/** A date, time or datetime in the reader's locale, as the applied filter tag shows it. */
function formatDate(value, type) {
  if (!value) return "";
  if (type === "time") {
    const d = new Date(`1970-01-01T${value}`);
    return Number.isNaN(+d) ? value : d.toLocaleTimeString(undefined, { timeStyle: "short" });
  }
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  // A plain date is read as a local date, or "2025-01-01" slips a day west of Greenwich.
  const d = parts ? new Date(+parts[1], +parts[2] - 1, +parts[3]) : new Date(value);
  if (Number.isNaN(+d)) return value;
  return type === "date"
    ? d.toLocaleDateString(undefined, { dateStyle: "short" })
    : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

// --- One option in a selection filter group --------------------------------

/**
 * <i80-filter-option value="project-1" label="Project 1" checked></i80-filter-option>
 *
 * A real checkbox (a radio in a single-select group) named after the group's key, so it
 * submits with the form. An <input> written inside is used as it is, which is how a
 * server-rendered form plugs in.
 */
export class I80FilterOption extends I80Element {
  static get observedAttributes() {
    return ["value", "label", "checked", "disabled", "name"];
  }

  get group() {
    return this.closest("i80-filter-group");
  }

  get input() {
    return this._input ?? null;
  }

  get value() {
    return this._input?.value ?? this.getAttribute("value") ?? "";
  }

  get checked() {
    return !!this._input?.checked;
  }

  get label() {
    return this.getAttribute("label") ?? this._label ?? this.value;
  }

  setup() {
    const type = this.group?.type === "single-select" ? "radio" : "checkbox";
    let box = this.part("option");
    let input;
    if (box) {
      input = box.querySelector("input");
      this._label = box.querySelector('[data-i80-part="text"]')?.textContent.trim() ?? "";
    } else {
      input = this.querySelector("input");
      this._label = this.getAttribute("label") ?? this.textContent.trim();
      if (!input) input = h("input", { type, value: this.getAttribute("value") ?? "" });
      if (this.flag("checked")) input.checked = true;
      box = h("label", { "data-i80-part": "option" }, input, h("span", { "data-i80-part": "text" }, this._label));
      this.replaceChildren(box);
    }
    // A clone carries the original's id; every instance gets its own, so <label for> holds.
    input.id = nextId("i80-filter-option");
    input.type = type;
    this._box = box;
    this._input = input;
  }

  update() {
    const input = this._input;
    if (!input) return;
    const name = this.getAttribute("name") ?? this.group?.key;
    if (name) input.name = name;
    if (this.hasAttribute("value")) input.value = this.getAttribute("value");
    if (this.hasAttribute("disabled")) input.disabled = this.flag("disabled");
    const kind = input.type === "radio" ? "radio" : "checkbox";
    input.className = `i80-form-${kind} i80-filter-bar__filter-group__selection-option__control`;
    this._box.className = [
      `i80-filter-bar__filter-group__${kind}`,
      "i80-filter-bar__filter-group__selection-option",
      "i80-typography-body-200",
      this._hidden && "i80-filter-bar__filter-group__selection-option--hidden",
    ].filter(Boolean).join(" ");
    this._box.setAttribute("for", input.id);
    const text = this._box.querySelector('[data-i80-part="text"]');
    text.className = "i80-text i80-typography-body-200 i80-font-weight-medium i80-filter-bar__filter-group__selection-option__text-content";
    if (this.hasAttribute("label")) text.textContent = this.getAttribute("label");
  }

  /** Hide the option while the group's option search does not match it. */
  match(query) {
    this._hidden = !!query && !this.label.toLowerCase().includes(query.toLowerCase());
    this.update();
  }
}

// --- One filter group: a tab in the dropdown and the panel beside it -------

/**
 * <i80-filter-group key="project" text="Project" type="multi-select" search>
 *   <i80-filter-option value="project-1" label="Project 1"></i80-filter-option>
 * </i80-filter-group>
 *
 * type="numerical" / "date" / "time" / "datetime" render the operator select and the value
 * inputs themselves (named key-selector, key-value, key-between-start, key-between-end);
 * selector, value, value-start and value-end say what is applied. type="generic" puts
 * whatever you write inside into the panel, so any control of your own can be a filter.
 */
export class I80FilterGroup extends I80Element {
  static get observedAttributes() {
    return ["key", "text", "type", "search", "selector", "value", "value-start", "value-end"];
  }

  get key() {
    return this.getAttribute("key") ?? "";
  }

  get text() {
    return this.getAttribute("text") ?? this.key;
  }

  get type() {
    return this.choice("type", GROUP_TYPES, "multi-select");
  }

  /**
   * The bar this group belongs to, once it is ready to be told about a change.
   *
   * A group can be set up before the bar is: registering a tag upgrades the elements
   * already on the page, and the bar is registered after the tags it builds. Until the bar
   * has upgraded there is nothing to tell — it reads every group when it sets up.
   */
  get bar() {
    const bar = this.closest("i80-filter-bar");
    return typeof bar?.refresh === "function" ? bar : null;
  }

  get panelId() {
    return this._panel?.id ?? "";
  }

  get options() {
    return [...this.querySelectorAll("i80-filter-option")];
  }

  setup() {
    let panel = this.part("panel");
    let options;
    let authored;
    if (panel) {
      // Markup we built before (a clone): take the author's pieces back out and rebuild.
      options = [...panel.querySelectorAll("i80-filter-option")];
      authored = [...(panel.querySelector('[data-i80-part="generic"]')?.childNodes ?? [])]
        .filter((n) => !(n.nodeType === Node.ELEMENT_NODE && n.hasAttribute("data-i80-part")));
    } else {
      const strays = this.takeContent();
      options = strays.filter((n) => n.nodeType === Node.ELEMENT_NODE && n.localName === "i80-filter-option");
      authored = strays.filter((n) => !options.includes(n));
      panel = h("section", { class: "i80-filter-bar__tabs__panel", role: "tabpanel", "data-i80-part": "panel", hidden: "" });
      this.append(panel);
    }
    this._panel = panel;
    panel.id = nextId("i80-filter-panel");
    this._build(options, authored);
    this.bar?.refresh();
  }

  adopt() {
    const list = this._panel.querySelector('[data-i80-part="list"]');
    const generic = this._panel.querySelector('[data-i80-part="generic"]');
    for (const node of this.takeContent()) {
      const option = node.nodeType === Node.ELEMENT_NODE && node.localName === "i80-filter-option";
      (option && list ? list : generic ?? this._panel).append(node);
    }
    this.bar?.refresh();
  }

  update() {
    for (const option of this.options) option.update();
  }

  _build(options, authored) {
    const type = this.type;
    const key = this.key;
    const parts = [];

    if (this.flag("search") && SELECTION.includes(type)) {
      const search = textInput({ type: "search", placeholder: T.search, "aria-label": T.groupSearch });
      search.addEventListener("input", () => {
        for (const option of this.options) option.match(search.value);
      });
      parts.push(h("div", { class: "i80-filter-bar__filter-group__search" }, search));
    }

    if (SELECTION.includes(type)) {
      parts.push(h("div", { class: "i80-filter-bar__filter-group__values-list" },
        this._clearButton(T.clearSelection),
        h("fieldset", { class: "i80-filter-bar__filter-group__list", "data-i80-part": "list" },
          h("legend", { class: "sr-only" }, T.selectionLegend), ...options)));
    } else if (type === "generic") {
      parts.push(h("div", { class: "i80-filter-bar__filter-group__generic", "data-i80-part": "generic" }, ...authored, ...options));
      parts.push(this._clearButton(T.clearFilter));
    } else {
      const numerical = type === "numerical";
      const fieldset = h("fieldset", { class: "i80-filter-bar__filter-group__fieldset" },
        h("legend", { class: "sr-only" }, numerical ? T.numberLegend : T.dateLegend),
        selectField(numerical ? T.numberLabel : T.dateLabel, { name: `${key}-selector` },
          numerical ? NUMERICAL : DATE, this.getAttribute("selector")));
      this._fieldset = fieldset;
      this._selector = fieldset.querySelector("select");
      // The bar listens for change on the tabs, so this only reshapes the inputs.
      this._selector.addEventListener("change", () => this._renderValues());
      parts.push(h("div", {
        class: `i80-filter-bar__filter-group__generic i80-filter-bar__filter-group__${numerical ? "numerical" : "date"}`,
        "data-i80-part": "generic",
      }, fieldset));
      parts.push(this._clearButton(T.clearFilter));
    }

    this._panel.replaceChildren(...parts);
    if (this._fieldset) this._renderValues();
    for (const option of options) option.update?.();
  }

  _clearButton(label) {
    const el = button(label, { color: "tertiary", icon: "rotate-ccw", extra: "i80-filter-bar__filter-group__clear-button" });
    el.addEventListener("click", () => {
      this.clearControls();
      this.changed();
    });
    return el;
  }

  /** The value inputs follow the operator: "between" wants two, anything else one. */
  _renderValues() {
    const fieldset = this._fieldset;
    const numerical = this.type === "numerical";
    const type = numerical ? "text" : INPUT_TYPE[this.type];
    const key = this.key;
    const field = "i80-filter-bar__filter-group__field";
    while (fieldset.children.length > 2) fieldset.lastElementChild.remove();
    if (this._selector.value === "between") {
      const direction = this.type === "datetime" ? "column" : "row";
      fieldset.append(h("div", { class: `i80-layout-flex i80-layout-flex--direction-${direction} i80-layout-flex--row-gap-8 i80-layout-flex--column-gap-8` },
        textInput({ type, name: `${key}-between-start`, value: this.getAttribute("value-start") ?? "", placeholder: T.start, "aria-label": numerical ? T.numberStart : T.dateStart }, `${field} ${field}--between`),
        textInput({ type, name: `${key}-between-end`, value: this.getAttribute("value-end") ?? "", placeholder: T.end, "aria-label": numerical ? T.numberEnd : T.dateEnd }, `${field} ${field}--between`)));
    } else {
      fieldset.append(textInput({
        type,
        name: `${key}-value`,
        value: this.getAttribute("value") ?? "",
        "aria-label": numerical ? T.numberValue : T.dateValue,
        ...(numerical ? { placeholder: T.numberPlaceholder } : {}),
      }, field));
    }
  }

  /** Every named control in the panel, in document order. */
  controls() {
    if (!this._panel) return [];
    return [...this._panel.querySelectorAll("input[name], select[name], textarea[name]")];
  }

  /** The applied filters this group holds: one per selected value. */
  applied() {
    const out = [];
    // A group the browser has not upgraded yet has nothing to report; the bar reads the
    // tabs again as soon as each group sets itself up.
    if (!this._panel) return out;
    const text = this.text;
    if (SELECTION.includes(this.type)) {
      for (const option of this.options) {
        if (option.input && option.checked) {
          out.push({ key: option.input.name || this.key, value: option.value, text: `${text}: ${option.label}`, controls: [option.input] });
        }
      }
      return out;
    }
    if (this.type === "generic") {
      for (const control of this.controls()) {
        const value = valueOf(control);
        if (value) out.push({ key: control.name, value, text: `${text}: ${value}`, controls: [control] });
      }
      return out;
    }
    const selector = this._selector?.value;
    if (!selector) return out;
    const numerical = this.type === "numerical";
    const word = (numerical ? NUMERICAL : DATE).find(([v]) => v === selector)?.[2] ?? selector;
    const show = (v) => (numerical ? v : formatDate(v, this.type));
    const inputs = [...this._fieldset.querySelectorAll("input")];
    if (selector === "between") {
      const [start, end] = inputs;
      if (!start?.value || !end?.value) return out;
      out.push({
        key: this.key,
        value: `${start.value}..${end.value}`,
        text: `${text} ${word} ${show(start.value)} ${T.and} ${show(end.value)}`,
        controls: [this._selector, start, end],
      });
    } else {
      const [input] = inputs;
      if (!input?.value) return out;
      out.push({ key: this.key, value: input.value, text: `${text} ${word} ${show(input.value)}`, controls: [this._selector, input] });
    }
    return out;
  }

  get count() {
    return this.applied().length;
  }

  clearControls() {
    for (const control of this.controls()) clearControl(control);
    if (this._fieldset) {
      this._selector.value = "";
      this._renderValues();
    }
    for (const option of this.options) option.match("");
  }

  /** A control here changed: the tab count follows, and a live bar re-filters. */
  changed() {
    this.bar?.changed();
  }

  setSelected(selected, tabId) {
    if (!this._panel) return;
    this._panel.hidden = !selected;
    if (tabId) this._panel.setAttribute("aria-labelledby", tabId);
  }
}

// --- The filters dropdown -------------------------------------------------

/**
 * The Filters dropdown that <i80-filter-bar> creates: the Dropdown, with the filter groups
 * as tabs inside it and Apply / Clear in its footer. Clicks in the tabs must not close it
 * (keep-open on the generic item), and the tabs answer the arrow keys themselves.
 */
export class I80FilterDropdown extends I80Dropdown {
  update() {
    super.update();
    this._root.classList.add("i80-filter-bar__filters-dropdown");
  }

  _onListKey() {}
}

// --- One applied filter the server rendered -------------------------------

/**
 * <i80-applied-filter key="owner" value="ada" href="/plans">Owner: Ada</i80-applied-filter>
 *
 * A filter the bar has no control for (applied elsewhere, or by a link). Dismissing it
 * fires i80-filter on the bar and, with href, goes to that URL — the URL without that one
 * parameter is something only the server can write.
 */
export class I80AppliedFilter extends I80Element {
  static get observedAttributes() {
    return ["key", "value", "text", "href"];
  }

  setup() {
    this._tag = this.part("tag");
    if (!this._tag) {
      this._tag = h("i80-tag", { dismissible: "", "dismiss-label": T.dismiss, "data-i80-part": "tag" }, ...this.takeContent());
      this.append(this._tag);
    }
    this.addEventListener("i80-dismiss", (e) => {
      if (e.target !== this._tag) return;
      this.closest("i80-filter-bar")?.dismiss({
        key: this.getAttribute("key") ?? "",
        value: this.getAttribute("value") ?? "",
        text: this._tag.textContent.trim(),
        controls: [],
        href: this.getAttribute("href"),
        element: this,
      });
    });
  }

  adopt() {
    this._tag.append(...this.takeContent());
  }

  update() {
    if (this.hasAttribute("text")) this._tag.setAttribute("text", this.getAttribute("text"));
  }
}

// --- The bar --------------------------------------------------------------

/**
 * <i80-filter-bar search action="/plans">
 *   <i80-filter-group key="project" text="Project" type="multi-select">
 *     <i80-filter-option value="project-1" label="Project 1"></i80-filter-option>
 *   </i80-filter-group>
 *   <i80-dropdown slot="actions" text="Actions" color="secondary" size="small">…</i80-dropdown>
 * </i80-filter-bar>
 *
 * With action or method (or a <form> written inside) the bar is that form; otherwise it is
 * a <div> and an enclosing <form> submits it. Applying, dismissing a tag and clearing fire
 * the cancelable i80-filter event and then submit; cancel it to filter in the page instead.
 */
export class I80FilterBar extends I80Element {
  static get observedAttributes() {
    return ["search", "search-name", "search-value", "search-placeholder", "search-aria-label", "action", "method",
      "live", "expanded", "filters-text", "apply-text", "clear-text", "no-filters-text", "dropdown-width", "dropdown-height"];
  }

  get groups() {
    return this._tabs ? [...this._tabs.querySelectorAll(":scope > i80-filter-group")] : [];
  }

  /** What the bar would send: { key: [value, …] }, the search included. */
  get filters() {
    const out = {};
    for (const { key, value } of this.applied) (out[key] ??= []).push(value);
    return out;
  }

  /** The applied filters, as the tags show them. */
  get applied() {
    return this._applied ?? [];
  }

  /** The form the bar submits: its own root, or the one it sits in. */
  get form() {
    return this._bar?.localName === "form" ? this._bar : this.closest("form");
  }

  setup() {
    let root = this.part("bar");
    if (root) {
      this._authorTags = [...this.querySelectorAll("i80-applied-filter")];
    } else {
      const ownForm = this.child("form");
      const strays = this.takeContent(ownForm ? [ownForm] : []);
      const element = (n) => n.nodeType === Node.ELEMENT_NODE;
      const groups = strays.filter((n) => element(n) && n.localName === "i80-filter-group");
      const tags = strays.filter((n) => element(n) && n.localName === "i80-applied-filter");
      const actions = strays.filter((n) => element(n) && !groups.includes(n) && !tags.includes(n));
      this._authorTags = tags;
      root = ownForm ?? (this.hasAttribute("action") || this.hasAttribute("method")
        ? h("form", { method: this.getAttribute("method") || "get", action: this.getAttribute("action") })
        : h("div"));
      root.setAttribute("data-i80-part", "bar");
      const dropdown = h("i80-filter-dropdown", { icon: "filter", text: T.filters, color: "secondary", size: "small", position: "bottom-left", width: "600px" });
      const tablist = h("ol", { class: "i80-filter-bar__tabs__list", role: "tablist", "aria-label": T.tabs, "data-i80-part": "tablist" });
      const tabs = h("div", { class: "i80-filter-bar__tabs", "data-i80-part": "tabs" }, tablist, ...groups);
      const apply = button(T.apply, { type: "submit", "data-i80-part": "apply" });
      const clear = button(T.clearAll, { color: "secondary", "data-i80-part": "clear" });
      const left = h("div", { class: "i80-filter-bar__actions__left" },
        button(T.toggle, {
          color: "secondary", icon: "unfold-open", iconOnly: true,
          extra: "i80-filter-bar__applied-filters-toggle-button", "aria-expanded": "false", "data-i80-part": "toggle",
        }),
        dropdown,
        this.flag("search") ? textInput({ type: "search", name: "search", "data-i80-part": "search" }, "i80-filter-bar__search") : null);
      root.append(
        h("span", { class: "sr-only", "data-i80-part": "live", hidden: "" }, T.live),
        h("div", {
          class: "i80-layout-flex i80-layout-flex--direction-row i80-layout-flex--align-items-center"
            + " i80-layout-flex--row-gap-8 i80-layout-flex--column-gap-8 i80-layout-flex--has-wrapping i80-filter-bar__actions",
          "data-i80-part": "row",
        }, left, ...actions),
        h("div", { class: "i80-filter-bar__applied-filters-list", "data-i80-part": "applied-list" }),
      );
      this.append(root);
      // The Dropdown builds its markup when it connects; the tabs and the footer go inside
      // it afterwards, so make sure that has happened (reaction order is not ours to assume).
      if (!dropdown._i80Ready) {
        dropdown._i80Ready = true;
        dropdown.setup();
      }
      const content = dropdown.querySelector(".i80-dropdown__content");
      content.querySelector(".i80-dropdown__list")
        .append(h("li", { class: "i80-dropdown-list-item i80-dropdown-list-item--variant-generic", "keep-open": "" }, tabs));
      content.append(h("div", { class: "i80-dropdown__footer i80-dropdown__footer--with-divider" },
        h("div", { class: "i80-button-set" }, apply, clear)));
    }

    this._bar = root;
    this._live = root.querySelector('[data-i80-part="live"]');
    this._row = root.querySelector('[data-i80-part="row"]');
    this._toggle = root.querySelector('[data-i80-part="toggle"]');
    this._dropdown = root.querySelector("i80-filter-dropdown");
    this._tabs = root.querySelector('[data-i80-part="tabs"]');
    this._tablist = root.querySelector('[data-i80-part="tablist"]');
    this._list = root.querySelector('[data-i80-part="applied-list"]');
    this._applyButton = root.querySelector('[data-i80-part="apply"]');
    this._search = root.querySelector('[data-i80-part="search"]');
    this._tab = 0;
    this._applied = null;
    for (const el of root.querySelectorAll('.i80-dropdown__footer [data-i80-part="clear"]')) {
      if (!el._i80Wired) {
        el._i80Wired = true;
        el.addEventListener("click", () => this.clear());
      }
    }

    // New ids on every instance, so a clone never points at the original's markup.
    this._list.id = nextId("i80-filter-applied");
    this._toggle.id = nextId("i80-filter-toggle");
    this._toggle.setAttribute("aria-controls", this._list.id);
    this._toggle.addEventListener("click", () => {
      this._expanded = !this._expanded;
      this._renderApplied();
    });
    this._applyButton.addEventListener("click", () => {
      this._dropdown?.hide();
      if (!this.form) this.apply();
    });
    this._tabs.addEventListener("change", () => this.changed());
    if (this._search) this._search.addEventListener("change", () => this._settle("search", {}, this.flag("live")));
    this.addEventListener("i80-dismiss", (e) => {
      if (e.target.localName === "i80-tag" && e.target._i80Filter) this.dismiss(e.target._i80Filter);
    });
    const form = this.form;
    if (form && !form._i80FilterBar) {
      form._i80FilterBar = this;
      form.addEventListener("submit", (e) => {
        // A submit we asked for has already announced itself as a dismiss, clear or change.
        if (this._submitting) return;
        this._refresh();
        if (!this._emit("apply", {})) e.preventDefault();
        this._dropdown?.hide();
      });
    }
  }

  /** Groups and tags can be written after the tag (a template engine, a streamed response). */
  adopt() {
    for (const node of this.takeContent()) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.localName === "i80-filter-group") this._tabs.append(node);
      else if (node.localName === "i80-applied-filter") this._authorTags.push(node);
      else this._row.append(node);
    }
    this._applied = null;
    this.update();
  }

  update() {
    const live = this.flag("live");
    this._bar.className = "i80-filter-bar";
    this._live.hidden = !live;
    this._applyButton.hidden = live;
    if (this._search) {
      this._search.name = this.getAttribute("search-name") || "search";
      this._search.placeholder = this.getAttribute("search-placeholder") ?? T.search;
      this._search.setAttribute("aria-label", this.getAttribute("search-aria-label") ?? this.getAttribute("search-placeholder") ?? T.search);
      if (this.hasAttribute("search-value")) this._search.value = this.getAttribute("search-value");
    }
    if (this._dropdown) {
      this._dropdown.setAttribute("text", this.getAttribute("filters-text") ?? T.filters);
      this._dropdown.setAttribute("width", this.getAttribute("dropdown-width") ?? "600px");
      this._dropdown.style.setProperty("--filter-bar-filters-dropdown-height", this.getAttribute("dropdown-height") ?? "600px");
    }
    this._applyButton.querySelector(".i80-button__text").textContent = this.getAttribute("apply-text") ?? T.apply;
    for (const el of this._bar.querySelectorAll('[data-i80-part="clear"] .i80-button__text')) {
      el.textContent = this.getAttribute("clear-text") ?? T.clearAll;
    }
    // The controls the server rendered are the filters it applied.
    if (!this._applied) {
      this._applied = this._snapshot();
      this._expanded = this.hasAttribute("expanded") ? this.flag("expanded") : this._applied.length > 0 || this._authorTags.length > 0;
    }
    this._renderTabs();
    this._renderApplied();
  }

  /** A group appeared or its options changed: read the applied filters again. */
  refresh() {
    this._applied = null;
    if (this._tabs) this.update();
  }

  _renderTabs() {
    const list = this._tablist;
    const groups = this.groups;
    while (list.children.length > groups.length) list.lastElementChild.remove();
    while (list.children.length < groups.length) {
      list.append(h("li", { class: "i80-filter-bar__tabs__tab", role: "presentation" },
        h("button", { class: "i80-filter-bar__tabs__tab__button", type: "button", role: "tab" })));
    }
    // A cloned tab arrives without its listeners and with the original's id.
    for (const tab of list.querySelectorAll(".i80-filter-bar__tabs__tab__button")) {
      if (tab._i80Wired) continue;
      tab._i80Wired = true;
      tab.id = nextId("i80-filter-tab");
      const index = () => [...list.children].indexOf(tab.parentElement);
      tab.addEventListener("click", () => this.selectTab(index()));
      tab.addEventListener("keydown", (e) => this._tabKey(e, index()));
    }
    this._tab = Math.min(this._tab, Math.max(groups.length - 1, 0));
    groups.forEach((group, i) => {
      const li = list.children[i];
      const tab = li.firstElementChild;
      const selected = i === this._tab;
      li.className = "i80-filter-bar__tabs__tab" + (selected ? " i80-filter-bar__tabs__tab--is-selected" : "");
      tab.setAttribute("aria-controls", group.panelId);
      tab.setAttribute("aria-selected", String(selected));
      if (selected) tab.removeAttribute("tabindex");
      else tab.setAttribute("tabindex", "-1");
      const count = group.count;
      tab.replaceChildren(
        h("span", { class: "i80-text i80-typography-body-200 i80-font-weight-medium i80-filter-bar__tabs__tab__text" }, group.text),
        ...(count > 0
          ? [h("span", { class: "sr-only" }, T.applied),
            h("span", { class: "i80-text i80-typography-body-200 i80-font-weight-regular i80-foreground-primary i80-filter-bar__tabs__tab__filter-count" }, String(count))]
          : []),
        icon("chevron-right", { className: "i80-foreground-primary i80-filter-bar__tabs__tab__icon" }),
      );
      group.setSelected(selected, tab.id);
    });
  }

  selectTab(index) {
    if (index < 0 || index === this._tab) return;
    this._tab = index;
    this._renderTabs();
  }

  _tabKey(e, index) {
    const n = this._tablist.children.length;
    const to = { ArrowDown: index + 1, ArrowRight: index + 1, ArrowUp: index - 1, ArrowLeft: index - 1, Home: 0, End: n - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    this._tablist.children[(to + n) % n].firstElementChild.focus();
  }

  _renderApplied() {
    const expanded = this._expanded;
    this._toggle.setAttribute("aria-expanded", String(expanded));
    this._toggle.replaceChildren(h("span", { class: "i80-button__icon" }, icon(expanded ? "unfold-close" : "unfold-open", { stretched: true })));
    // Tags the author wrote are kept; only the ones read off the controls are rebuilt.
    for (const tag of this._authorTags) tag.remove();
    this._list.replaceChildren();
    if (!expanded) return;
    this._list.append(h("hr", { class: "i80-separator i80-separator--spacing-0" }));
    const content = h("div", { class: "i80-filter-bar__applied-filters-list__content" });
    this._list.append(content);
    if (!this.applied.length && !this._authorTags.length) {
      content.append(h("span", { class: "i80-text i80-typography-body-100 i80-font-weight-regular i80-foreground-faint" },
        this.getAttribute("no-filters-text") ?? T.none));
      return;
    }
    const tags = h("div", { class: "i80-filter-bar__applied-filters" }, ...this._authorTags);
    for (const filter of this.applied) {
      const tag = h("i80-tag", { dismissible: "", "dismiss-label": T.dismiss, text: filter.text, "data-i80-part": "tag" });
      tag._i80Filter = filter;
      tags.append(tag);
    }
    const clear = button(this.getAttribute("clear-text") ?? T.clearAll, { color: "tertiary", icon: "x", extra: "i80-filter-bar__clear-button", "data-i80-part": "clear" });
    clear.addEventListener("click", () => this.clear());
    content.append(tags, clear);
  }

  /** A control changed: the tab counts follow, and a live bar applies straight away. */
  changed() {
    this._renderTabs();
    if (this.flag("live")) this._settle("change");
  }

  /** Read the applied filters off the controls again and show them. */
  _refresh() {
    this._applied = this._snapshot();
    if (!this.hasAttribute("expanded")) this._expanded = this._applied.length > 0 || this._authorTags.length > 0;
    this._renderTabs();
    this._renderApplied();
  }

  _snapshot() {
    const out = [];
    const search = this._search;
    if (search && search.value.trim()) {
      out.push({ key: search.name, value: search.value, text: `${T.search}: ${search.value}`, controls: [search] });
    }
    for (const group of this.groups) out.push(...group.applied());
    return out;
  }

  _emit(reason, extra) {
    return this.dispatchEvent(new CustomEvent("i80-filter", {
      bubbles: true,
      cancelable: true,
      detail: { filters: this.filters, applied: this.applied, reason, ...extra },
    }));
  }

  /** Show the new state, tell the page, and submit unless the page says no. */
  _settle(reason, extra = {}, submit = true) {
    this._refresh();
    if (this._emit(reason, extra) && submit) this._submit();
  }

  _submit() {
    const form = this.form;
    if (!form) return;
    this._submitting = true;
    try {
      form.requestSubmit();
    } finally {
      this._submitting = false;
    }
  }

  /** Remove one applied filter: its control is unset, then the bar re-filters. */
  dismiss(filter) {
    for (const control of filter.controls) clearControl(control);
    if (filter.element) {
      filter.element.remove();
      this._authorTags = this._authorTags.filter((tag) => tag !== filter.element);
    }
    this._refresh();
    if (!this._emit("dismiss", { key: filter.key, value: filter.value })) return;
    if (filter.href) {
      location.href = filter.href;
      return;
    }
    this._submit();
    this._dropdown?.querySelector(".i80-dropdown-toggle-button")?.focus();
  }

  /** Apply what is selected: the same as pressing "Apply filters". */
  apply() {
    const form = this.form;
    if (form) {
      form.requestSubmit();
      return;
    }
    this._refresh();
    this._emit("apply", {});
    this._dropdown?.hide();
  }

  clear() {
    for (const group of this.groups) group.clearControls();
    if (this._search) this._search.value = "";
    for (const tag of [...this._authorTags]) tag.remove();
    this._authorTags = [];
    this._dropdown?.hide();
    this._settle("clear");
    this._dropdown?.querySelector(".i80-dropdown-toggle-button")?.focus();
  }
}

// Leaves first, the bar last. Registering a tag upgrades the elements already on the page
// there and then, and each of these reaches for the ones below it: the bar builds a
// dropdown, a group updates its options. Registered the other way round, a page that
// arrived before the script came up half-built. See test/server-rendered.spec.js.
define("i80-filter-option", I80FilterOption);
define("i80-filter-group", I80FilterGroup);
define("i80-filter-dropdown", I80FilterDropdown);
define("i80-applied-filter", I80AppliedFilter);
define("i80-filter-bar", I80FilterBar);
